import { Controller } from "@hotwired/stimulus"
import SlimSelect from "slim-select"

// Connects to data-controller="form-select"
export default class extends Controller {
  connect() {
    const placeholder = this.element.dataset.placeholder || "Please Select"

    this.isOpen = false
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)

    document.addEventListener("keydown", this.handleDocumentKeydown)

    this.slimSelect = new SlimSelect({
      select: this.element,
      settings: {
        hideSelected: true,
        closeOnSelect: false,
        placeholderText: placeholder,
        showSearch: this.element.dataset.showSearch != 'false',
        searchPlaceholder: placeholder,
        maxValuesShown: Number(this.element.dataset.maxValuesShown || 20)
      },
      events: {
        searchFilter: (option, search) => {
          const term = search.trim()

          const range = this.rangeBoundEnabled() ? this.parseRange(term) : null
          if (range) {
            const optionNumber = this.optionNumber(option)
            return optionNumber !== null && optionNumber >= range.start && optionNumber <= range.end
          }

          const commaTokens = this.commaBoundEnabled() ? this.parseCommaTokens(term) : null
          if (commaTokens) {
            return commaTokens.some((token) => this.optionMatchesToken(option, token))
          }

          return (option.text || "").toLowerCase().includes(term.toLowerCase())
        },

        afterOpen: () => {
          this.isOpen = true
        },

        afterClose: () => {
          this.isOpen = false
        }
      }
    })
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleDocumentKeydown)
    this.slimSelect?.destroy()
  }

  rangeBoundEnabled() {
    return this.element.dataset.rangeBound === "true"
  }

  commaBoundEnabled() {
    return this.element.dataset.commaBound === "true"
  }

  handleDocumentKeydown(event) {
    if (!this.isOpen) return
    if (event.key !== "Enter") return

    const input = document.activeElement
    if (!(input instanceof HTMLInputElement)) return
    if (!input.closest(".ss-search")) return

    const term = input.value.trim()

    const range = this.rangeBoundEnabled() ? this.parseRange(term) : null
    const commaTokens = !range && this.commaBoundEnabled() ? this.parseCommaTokens(term) : null

    if (!range && !commaTokens) return

    event.preventDefault()

    const selectedValues = []

    const walk = (items) => {
      for (const item of items) {
        if (item.options) {
          walk(item.options)
          continue
        }

        if (range) {
          const optionNumber = this.optionNumber(item)
          if (optionNumber !== null && optionNumber >= range.start && optionNumber <= range.end) {
            selectedValues.push(String(item.value))
          }
          continue
        }

        if (commaTokens && commaTokens.some((token) => this.optionMatchesToken(item, token))) {
          selectedValues.push(String(item.value))
        }
      }
    }

    walk(this.slimSelect.getData())

    if (selectedValues.length === 0) return

    const currentSelected = this.normalizeSelected(this.slimSelect.getSelected())
    const mergedValues = [...new Set([...currentSelected, ...selectedValues])]

    this.slimSelect.setSelected(mergedValues)
  }

  parseRange(value) {
    const match = value.match(/^(\d+)\s*-\s*(\d+)$/)
    if (!match) return null

    let start = Number(match[1])
    let end = Number(match[2])

    if (start > end) [start, end] = [end, start]

    return { start, end }
  }

  parseCommaTokens(value) {
    if (!value.includes(",")) return null

    const tokens = value
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean)

    return tokens.length > 0 ? tokens : null
  }

  optionMatchesToken(option, token) {
    const normalizedToken = token.trim().toLowerCase()
    const tokenNumber = /^\d+$/.test(normalizedToken) ? Number(normalizedToken) : null

    const text = String(option.text || "").trim()
    const value = String(option.value || "").trim()

    if (text.toLowerCase() === normalizedToken) return true
    if (value.toLowerCase() === normalizedToken) return true

    if (tokenNumber !== null) {
      const optionNumber = this.optionNumber(option)
      if (optionNumber !== null && optionNumber === tokenNumber) return true
    }

    return false
  }

  optionNumber(option) {
    const candidates = [option.text, option.value].filter(Boolean)

    for (const candidate of candidates) {
      const text = String(candidate).trim()

      if (/^\d+$/.test(text)) {
        return Number(text)
      }

      const match = text.match(/\d+/)
      if (match) {
        return Number(match[0])
      }
    }

    return null
  }

  normalizeSelected(selected) {
    if (!selected) return []
    return Array.isArray(selected) ? selected.map(String) : [String(selected)]
  }
}
